import { spaceContains } from './spaces.jsx';

// «ورودی»: نقطه‌ای که بازدیدِ سه‌بعدی از آن‌جا شروع می‌شود (روی کاغذ: کلمه‌ی «ورودی» بیرونِ خانه کنارِ دیوار).
// spaces: خروجیِ computeSpaces (مختصاتِ x و y پلان؛ spaceContains(sp, x, y))

export const insideAny = (spaces, x, y) => (spaces || []).some((sp) => spaceContains(sp, x, y));

// نزدیک‌ترین نقطه‌ی پاره‌خط به p
export function projectOnWall(w, px, py) {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const len2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((px - w.x1) * dx + (py - w.y1) * dy) / len2));
  const x = w.x1 + dx * t;
  const y = w.y1 + dy * t;
  return { t, x, y, dist: Math.hypot(px - x, py - y), len: Math.sqrt(len2) };
}

// بردارِ عمود به سمتِ داخلِ خانه در نقطه‌ی (x,y) روی دیوارِ w؛ eps = فاصله‌ی نمونه‌برداری از دو طرف.
// خروجی { nx, ny, exterior } — exterior یعنی یک طرف داخلِ فضا و طرفِ دیگر بیرونِ همه‌ی فضاهاست
export function inwardNormal(w, x, y, spaces, eps) {
  const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
  const ux = (w.x2 - w.x1) / len;
  const uy = (w.y2 - w.y1) / len;
  const ax = -uy;
  const ay = ux;
  const inA = insideAny(spaces, x + ax * eps, y + ay * eps);
  const inB = insideAny(spaces, x - ax * eps, y - ay * eps);
  if (inA && !inB) return { nx: ax, ny: ay, exterior: true };
  if (inB && !inA) return { nx: -ax, ny: -ay, exterior: true };
  return { nx: ax, ny: ay, exterior: false };
}

// نزدیک‌ترین دیوارِ بیرونی به نقطه‌ی p (اگر هیچ فضای بسته‌ای نیست، نزدیک‌ترین دیوار) — { index, x, y, t, nx, ny }
export function nearestExteriorWall(walls, spaces, px, py, eps, minLen = 0) {
  let best = null;
  walls.forEach((w, index) => {
    const pr = projectOnWall(w, px, py);
    if (pr.len < minLen) return;
    const n = inwardNormal(w, pr.x, pr.y, spaces, eps);
    const ext = n.exterior || !(spaces && spaces.length);
    const score = pr.dist + (ext ? 0 : 1e9);
    if (!best || score < best.score) best = { index, score, ...pr, nx: n.nx, ny: n.ny, exterior: ext };
  });
  return best;
}

// «ورودی» یک نقطه است (جای ایستادن در شروعِ بازدید)، نه یک در. درِ خودِ خانه را کاربر می‌گذارد؛
// نزدیک‌ترین در به این نقطه (تا ۳ متر) از اول باز است — انگار همین الان دارند وارد می‌شوند.
// خروجی: { x, z, yaw, doorId } (yaw با قراردادِ دوربین: جلو = (−sin, −cos))
export function entranceSpawn(entrance, walls, doors, spaces) {
  if (!entrance || !Number.isFinite(entrance.x) || !Number.isFinite(entrance.y)) return null;
  const ws = walls || [];
  let door = null;
  (doors || []).forEach((d) => {
    const w = ws.find((x) => x.id === d.wallId);
    if (!w) return;
    const x = w.x1 + (w.x2 - w.x1) * d.ratio;
    const y = w.y1 + (w.y2 - w.y1) * d.ratio;
    const dist = Math.hypot(x - entrance.x, y - entrance.y);
    if (dist <= 300 && (!door || dist < door.dist)) door = { id: d.id, x, y, dist };
  });
  // رو به کجا: بیرونِ خانه و کنارِ یک در → رو به همان در؛ وگرنه رو به وسطِ خانه
  let tx;
  let ty;
  if (door && !insideAny(spaces, entrance.x, entrance.y) && door.dist > 20) {
    tx = door.x;
    ty = door.y;
  } else if (ws.length) {
    const xs = ws.flatMap((w) => [w.x1, w.x2]);
    const ys = ws.flatMap((w) => [w.y1, w.y2]);
    tx = (Math.min(...xs) + Math.max(...xs)) / 2;
    ty = (Math.min(...ys) + Math.max(...ys)) / 2;
  } else {
    tx = entrance.x;
    ty = entrance.y - 1;
  }
  const dx = tx - entrance.x;
  const dy = ty - entrance.y;
  return {
    x: entrance.x,
    z: entrance.y,
    yaw: Math.hypot(dx, dy) > 1 ? Math.atan2(-dx, -dy) : 0,
    doorId: door ? door.id : null,
  };
}

// پلانِ کاغذ: نوشته‌ای که بیرونِ همه‌ی فضاهای بسته و نزدیکِ یک دیوارِ بیرونی است = «ورودی».
// (اسمِ اتاق‌ها داخلِ اتاق نوشته می‌شوند؛ بنابراین فقط جای نوشته کافی است — بدونِ خواندنِ متن)
// labels: { cx, cy, x0, y0, x1, y1 } پیکسلی؛ خروجی: { x, y } = وسطِ همان نوشته، یا null
export function guessEntrance(walls, spaces, labels, minSide) {
  if (!walls.length || !(spaces && spaces.length) || !(labels && labels.length)) return null;
  const eps = minSide * 0.012;
  const maxDist = minSide * 0.22;
  let best = null;
  labels.forEach((l) => {
    if (insideAny(spaces, l.cx, l.cy)) return;
    const hit = nearestExteriorWall(walls, spaces, l.cx, l.cy, eps);
    if (!hit || !hit.exterior) return;
    // فاصله از لبه‌ی مستطیلِ نوشته (نه از مرکزش) — کلمه‌ی بلند هم کنارِ دیوار حساب شود
    const gx = Math.max(l.x0 - hit.x, 0, hit.x - l.x1);
    const gy = Math.max(l.y0 - hit.y, 0, hit.y - l.y1);
    const d = Math.hypot(gx, gy);
    if (d > maxDist) return;
    if (!best || d < best.d) best = { d, x: l.cx, y: l.cy };
  });
  return best ? { x: best.x, y: best.y } : null;
}

// نوشته‌ای که به دیوارِ بیرونی چسبیده، با دیوار یکی می‌شود و یک «دیوارِ کوتاهِ آویزان» بیرونِ خانه می‌سازد.
// این تکه‌ها (کوتاه، فقط یک سرشان به دیوار رسیده، بیرونِ همه‌ی فضاها) دیوار نیستند → حذف، و جایشان یک «نوشته»
export function pruneOutsideStubs(walls, spaces, minSide) {
  if (!(spaces && spaces.length)) return { walls, stubs: [] };
  const maxLen = minSide * 0.1;
  const tol = minSide * 0.012;
  const touches = (i, x, y) => walls.some((o, j) => j !== i && projectOnWall(o, x, y).dist <= tol);
  const keep = [];
  const stubs = [];
  walls.forEach((w, i) => {
    const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
    const mx = (w.x1 + w.x2) / 2;
    const my = (w.y1 + w.y2) / 2;
    if (len < maxLen && touches(i, w.x1, w.y1) !== touches(i, w.x2, w.y2) && !insideAny(spaces, mx, my)) {
      const p = tol;
      stubs.push({
        x0: Math.min(w.x1, w.x2) - p,
        y0: Math.min(w.y1, w.y2) - p,
        x1: Math.max(w.x1, w.x2) + p,
        y1: Math.max(w.y1, w.y2) + p,
        cx: mx,
        cy: my,
      });
    } else keep.push(w);
  });
  return { walls: keep, stubs };
}
